<script lang="ts" setup>
import CounterCard from '@/components/CounterCard.vue';
import { useInventoryManager } from '@/services/inventoryManager';
import { useStateStore } from '@/services/state';
import { computed, onMounted, ref } from 'vue';
import BaseModal from '@/components/modals/BaseModal.vue';

// Services
const inv = useInventoryManager();
const stateStore = useStateStore();

const currentPageTitle = 'Out of Sync Products';
const switchButtonTitle = 'Custom SKU Management';

// Counter Cards
const shopifyVariantsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.shopifyPrice != null).length);
const acumaticaSKUsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.acumaticaPrice != null).length);
const nonAcumaticaSKUsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.shopifyOnly === true).length);
const nonShopifySKUsCount = computed(() => inv.consolidatedSKUs.value.filter((sku) => sku.acumaticaOnly === true).length);

const displayCounterCardInfo = ref(false);
const counterCardTitle = ref('');
const counterCardSkus = ref<string[] | null>(null);

function displayNonAcumaticaSKUs() {
	if (isBusy.value) return;
	counterCardSkus.value = inv.consolidatedSKUs.value.filter((item) => item.shopifyOnly === true).map((item) => item.sku);
	counterCardTitle.value = 'Non-Acumatica SKUs';
	displayCounterCardInfo.value = true;
}

function displayNonShopifySKUs() {
	if (isBusy.value) return;
	counterCardSkus.value = inv.consolidatedSKUs.value.filter((item) => item.acumaticaOnly === true).map((item) => item.sku);
	counterCardTitle.value = 'Non-Shopify SKUs';
	displayCounterCardInfo.value = true;
}

function closeCounterCardInfo() {
	displayCounterCardInfo.value = false;
	counterCardTitle.value = '';
	counterCardSkus.value = null;
}

// Table
const syncProduct_columnHeadings = ['Parent SKU', 'SKUs Affected', 'Effect', 'Acumatica', 'Shopify'];

function onRowClick(parentCode: string, e: PointerEvent) {
	if (isBusy.value) return;

	if (e.ctrlKey) {
		if (inv.selectedSKUs.value.has(parentCode)) {
			inv.selectedSKUs.value.delete(parentCode);
		} else {
			inv.selectedSKUs.value.add(parentCode);
		}
	} else {
		inv.selectedSKUs.value = new Set([parentCode]);
	}
}

// Data Loading and Status
const loadingSKUs = ref(false);
const updatingSKUs = ref(false);
const isBusy = computed(() => loadingSKUs.value || updatingSKUs.value);
const updatesNeeded = computed(() => inv.updatesNeededByParentCode.value.size > 0);

const status = computed(() => {
	if (updatingSKUs.value) return `Updating: ${inv.currentlyUpdatingSKU.value}`;
	if (loadingSKUs.value) return 'Loading...';
	if (!updatesNeeded.value) return 'No Updates Needed';
	if (inv.selectedSKUs.value.size === 0) return '';
	return `Products Selected: ${inv.selectedSKUs.value.size}`;
});

async function loadConsolidatedSKUs() {
	loadingSKUs.value = true;
	inv.selectedSKUs.value = new Set();
	inv.consolidatedSKUs.value = [];

	try {
		await inv.fetchConsolidatedSKUs();
	} catch (error) {
		alert('An error occurred while loading SKUs. Please refresh the page to try again.');
	}
	loadingSKUs.value = false;
}

async function triggerUpdate(updateType: 'all' | 'price' | 'quantity') {
	if (isBusy.value) return;

	if (inv.selectedSKUs.value.size === 0) {
		const shouldUpdateAll = confirm('No SKUs are selected. Continue and update all pending SKUs?');
		if (!shouldUpdateAll) {
			return;
		}
	}

	const updateNecessary = inv.isUpdateNecessary.value[updateType];
	if (!updateNecessary) {
		alert('Update was not necessary for the selected update type.');
		return;
	}

	const updateValid = inv.isUpdateValid.value[updateType];
	if (!updateValid) {
		const shouldContinue = confirm('One or more price updates will set price to 0 while Acumatica quantity is greater than 0. Continue with update?');
		if (!shouldContinue) {
			return;
		}
	}

	updatingSKUs.value = true;
	stateStore.navWarningActive = true;
	try {
		await inv.triggerUpdate(updateType);
	} catch (error) {
		alert('An error occurred during the update process. Please try again.');
	}
	stateStore.navWarningActive = false;
	updatingSKUs.value = false;

	await loadConsolidatedSKUs();
}

onMounted(async () => {
	await loadConsolidatedSKUs();
});
</script>

<template>
	<div class="inventory-view">
		<!-- Left Side -->

		<div class="inventory-view__left">
			<!-- Title and Search Bar -->

			<div class="left__header">
				<div class="header__titles">
					<h1 class="fira aliceblue">Inventory Manager</h1>
					<p class="fira aliceblue semi-large">{{ currentPageTitle }}</p>
				</div>
				<input v-model="inv.searchTerm.value" placeholder="Search..." />
			</div>

			<!-- Table Header and Table Body -->

			<div class="content__table-header">
				<table>
					<colgroup>
						<col style="width: 25%" />
						<col style="width: 39%" />
						<col style="width: 10%" />
						<col style="width: 13%" />
						<col style="width: 13%" />
					</colgroup>
					<thead>
						<tr>
							<th v-for="syncProduct_columnHeading in syncProduct_columnHeadings" :key="syncProduct_columnHeading">
								{{ syncProduct_columnHeading }}
							</th>
						</tr>
					</thead>
				</table>
			</div>
			<div class="content__table-container" :class="{ disabled: isBusy }">
				<table>
					<colgroup>
						<col style="width: 25%" />
						<col style="width: 39%" />
						<col style="width: 10%" />
						<col style="width: 13%" />
						<col style="width: 13%" />
					</colgroup>
					<tbody>
						<tr
							v-for="updates in inv.rowData.value"
							:key="updates.parentCode"
							:class="{ selected: inv.selectedSKUs.value.has(updates.parentCode) }"
							@click="onRowClick(updates.parentCode, $event)"
						>
							<td>{{ updates.parentCode }}</td>
							<td>{{ updates.skus }}</td>
							<td>{{ updates.effects }}</td>
							<td>{{ updates.acumaticaValues }}</td>
							<td>{{ updates.shopifyValues }}</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="pagination-buttons">
				<button :disabled="inv.isFirstPage.value" @click="inv.firstPage" class="icon-button">
					<span class="material-symbols-outlined">first_page</span>
				</button>
				<button :disabled="inv.isFirstPage.value" @click="inv.prevPage" class="icon-button">
					<span class="material-symbols-outlined">chevron_left</span>
				</button>
				<p>{{ inv.currentPage.value }} / {{ inv.totalPages.value }}</p>
				<button :disabled="inv.isLastPage.value" @click="inv.nextPage" class="icon-button">
					<span class="material-symbols-outlined">chevron_right</span>
				</button>
				<button :disabled="inv.isLastPage.value" @click="inv.lastPage" class="icon-button">
					<span class="material-symbols-outlined">last_page</span>
				</button>
			</div>

			<!-- Update Buttons and Status Indicator -->

			<div class="content__update-status">
				<button :disabled="isBusy" @click="loadConsolidatedSKUs">Reset</button>
				<button :disabled="isBusy || !updatesNeeded" @click="triggerUpdate('all')">Update</button>
				<button :disabled="isBusy || !updatesNeeded" @click="triggerUpdate('price')">Update Price Only</button>
				<button :disabled="isBusy || !updatesNeeded" @click="triggerUpdate('quantity')">Update Quantity Only</button>
				<p class="status">{{ status }}</p>
			</div>
		</div>

		<!-- Right Side -->

		<div class="inventory-view__right">
			<div class="right__button">
				<button>{{ switchButtonTitle }}</button>
			</div>
			<div class="right__cards">
				<CounterCard :title="'Shopify Variants: '" :value="loadingSKUs ? '-' : shopifyVariantsCount" />
				<CounterCard :title="'Acumatica SKUs: '" :value="loadingSKUs ? '-' : acumaticaSKUsCount" />
				<CounterCard
					:title="'Non-Acumatica SKUs: '"
					:value="loadingSKUs ? '-' : nonAcumaticaSKUsCount"
					:action-disabled="isBusy"
					@expand="displayNonAcumaticaSKUs"
					:icon="'notes'"
				/>
				<CounterCard
					:title="'Non-Shopify SKUs: '"
					:value="loadingSKUs ? '-' : nonShopifySKUsCount"
					:action-disabled="isBusy"
					@expand="displayNonShopifySKUs"
					:icon="'notes'"
				/>
			</div>
		</div>

		<!-- SKU Display Modal-->
		<BaseModal v-if="displayCounterCardInfo && counterCardSkus" @close="closeCounterCardInfo" :quick-exit="true">
			<p class="bold semi-large underline">{{ counterCardTitle }}</p>
			<p v-for="(sku, index) in counterCardSkus" :key="index">{{ index + 1 }}: {{ sku }}</p>
		</BaseModal>
	</div>
</template>

<style scoped>
.inventory-view {
	max-height: 100vh;
	display: flex;
	overflow-y: auto;
	user-select: none;
	gap: 15px;
}

/* Left Side */

.inventory-view__left {
	display: flex;
	flex-direction: column;
	height: 100vh;
	margin-left: 15px;
}

.left__header {
	display: flex;
	justify-content: space-between;
	margin-top: 15px;
}

.header__titles {
	h1 {
		font-weight: normal;
		margin: 0;
		font-size: 1.5rem;
	}

	p {
		color: rgb(149, 156, 161);
		font-size: 1.2rem;
	}
}

.content__table-header {
	background: #4b525a;
	border-radius: 5px 5px 0 0;
	margin-top: 5px;
}

.content__table-container {
	max-height: 100%;
	overflow-y: auto;
	background-color: #4b525a;
	flex: 1;
}

.content__table-container.disabled {
	pointer-events: none;
	opacity: 0.6;
}

.content__update-status {
	display: flex;
	padding: 15px 0 15px 15px;
	gap: 15px;
}

/* Right */

.inventory-view__right {
	display: flex;
	flex-direction: column;
	margin-right: 10px;
}

.right__button {
	flex: none;
	display: flex;
	justify-content: end;
	margin-top: 15px;
}

.right__cards {
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 15px;
	flex: 1 1 auto;
}

/* Table */

table {
	width: 100%;
	border-collapse: collapse;
	background: transparent;
	font-family: 'Fira Sans', serif;
	flex: 1;
	table-layout: fixed;
}

thead {
	border-bottom: 1px solid #272b2f;
	text-align: left;
	color: aliceblue;
	top: 0;
	position: sticky;
	opacity: 100;
}

thead th {
	padding: 20px 30px;
	font-weight: normal;
}

tbody {
	max-height: 100%;
	overflow-y: auto;
}

tbody td {
	padding: 20px 30px;
	white-space: pre-line;
	font-weight: 200;
	color: aliceblue;
}

.content__table-container tbody td:first-child {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

tbody tr:hover {
	background: #646e79;
	cursor: pointer;
}

tbody tr.selected {
	background: #646e79;
}

/* Input */

input {
	width: 250px;
	font-family: 'Fira Sans';
	height: fit-content;
	border: #272b2f 2px solid;
	font-size: 0.75rem;
	padding: 2px 5px;
	border-radius: 5px;
	box-sizing: border-box;
	align-self: flex-end;
}

input:focus {
	border: #4b525a 2px solid;
	outline: none;
}

/* Others */

.status {
	font-family: 'Fira Sans';
	color: #8cacd6;
	height: max-content;
	align-self: center;
}

.pagination-buttons {
	background-color: #4b525a;
	border-radius: 0 0 5px 5px;
	display: flex;

	justify-content: flex-end;
	padding-right: 15px;

	border-top: 1px solid #272b2f;

	p {
		margin: 5px 5px 0 5px;
		color: rgb(189, 195, 201);
	}
}
</style>
